import type { FC, ReactNode } from 'react'
import type { AccessRole } from '@/types'

import { useState } from 'react'
import { ArrowRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { Accent, SectionHeading } from '@/components/landing/section-heading'

import { plans } from '@/constants/landing'

interface PricingSectionProps {
    onChoose: (role: AccessRole | null) => void
}

type Billing = 'monthly' | 'yearly'

export const PricingSection: FC<PricingSectionProps> = ({ onChoose }): ReactNode => {
    const [billing, setBilling] = useState<Billing>('yearly')

    return (
        <section id='pricing' className='scroll-mt-20 border-t border-hairline py-24 sm:py-32'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <Reveal className='flex flex-col items-start gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        index="06"
                        label="Pricing"
                        title={<>Free for curious minds. <Accent>Fair for everyone else.</Accent></>}
                    />

                    <div role='group' aria-label="Billing period" className='inline-flex shrink-0 rounded-full border border-border bg-secondary p-1'>
                        {(['monthly', 'yearly'] as Billing[]).map(option => (
                            <button
                                key={option}
                                type='button'
                                aria-pressed={billing === option}
                                onClick={() => setBilling(option)}
                                className={cn(
                                    'flex h-9 items-center gap-2 rounded-full px-4 text-sm font-medium transition-all',
                                    billing === option ? 'bg-white text-ink shadow-[0_1px_2px_rgba(13,14,20,0.08),0_4px_12px_-4px_rgba(13,14,20,0.18)]' : 'text-muted-foreground hover:text-ink'
                                )}
                            >
                                {option === 'monthly' ? "Monthly" : "Yearly"}

                                {option === 'yearly' && (
                                    <span className='rounded-full bg-brand-soft px-1.5 py-0.5 text-[11px] font-semibold text-brand'>
                                        -25%
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <div className='mt-14 grid gap-4 lg:grid-cols-3 lg:gap-5'>
                    {plans.map((plan, index) => {
                        const price = billing === 'monthly' ? plan.monthly : plan.yearly
                        const featured = plan.featured

                        return (
                            <Reveal key={plan.name} delay={index * 110} className='h-full'>
                                <article
                                    className={cn(
                                        'relative flex h-full flex-col rounded-[28px] border p-7 sm:p-8',
                                        featured ? 'border-ink bg-ink text-white elevate-lg' : 'border-border/80 bg-white text-ink elevate'
                                    )}
                                >
                                    {featured && (
                                        <span className='absolute right-6 top-6 rounded-full bg-white/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-white'>
                                            Most popular
                                        </span>
                                    )}

                                    <h3 className='text-lg font-semibold tracking-[-0.01em]'>
                                        {plan.name}
                                    </h3>

                                    <p className={cn('mt-2 text-[15px] leading-relaxed', featured ? 'text-white/65' : 'text-muted-foreground')}>
                                        {plan.description}
                                    </p>

                                    <div className='mt-8 flex items-end gap-2'>
                                        {price === null ? (
                                            <span className='font-heading text-5xl font-semibold leading-none tracking-[-0.04em]'>
                                                Custom
                                            </span>
                                        ) : (
                                            <span className='font-heading text-5xl font-semibold leading-none tracking-[-0.04em] tabular-nums'>
                                                ${price}
                                            </span>
                                        )}

                                        <span className={cn('pb-1 text-sm', featured ? 'text-white/60' : 'text-muted-foreground')}>
                                            {price === null || price === 0 ? plan.priceNote : billing === 'yearly' ? "per month, billed yearly" : plan.priceNote}
                                        </span>
                                    </div>

                                    <a
                                        href='#access'
                                        onClick={() => onChoose(plan.role)}
                                        className={cn(
                                            'group mt-8 inline-flex h-12 items-center justify-center gap-2 rounded-full text-[15px] font-medium transition-colors',
                                            featured ? 'bg-white text-ink hover:bg-white/90' : 'border border-border bg-white text-ink hover:border-ink/25 hover:bg-secondary'
                                        )}
                                    >
                                        {plan.cta}
                                        <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' />
                                    </a>

                                    <ul className={cn('mt-8 flex flex-col gap-3 border-t pt-8 text-[15px]', featured ? 'border-white/10' : 'border-hairline')}>
                                        {plan.features.map(feature => (
                                            <li key={feature} className='flex items-start gap-3'>
                                                <span
                                                    className={cn(
                                                        'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full',
                                                        featured ? 'bg-brand text-white' : 'bg-brand-soft text-brand'
                                                    )}
                                                >
                                                    <Check className='size-3' strokeWidth={3} />
                                                </span>

                                                <span className={featured ? 'text-white/85' : 'text-muted-foreground'}>
                                                    {feature}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            </Reveal>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}
